import { addHours, addYears, endOfQuarter, setISOWeek, setQuarter, startOfISOWeek } from 'date-fns';
import { fmt, now, tzd } from '@/utils/dates';
import type { ChartGranularity, ChartType } from '../Store/store';

/**
 * These area used to calculate statistics finish dates for scertain parameters
 * and descide if chart settings are disabled or not.
 */
class Chart {
  year: number;
  granularity: ChartGranularity;
  chart: ChartType;

  constructor(year: number, granularity: ChartGranularity, chart: ChartType) {
    this.year = year;
    this.granularity = granularity;
    this.chart = chart;
  }

  getMonth = (year: number, month: string): Date => tzd(year, Number(month) - 1, 1);

  getEndOfQuarter = (year: number, quarter: number): Date => endOfQuarter(setQuarter(tzd(year, 0, 1), quarter));

  getNextYear = (year: number): Date => addYears(tzd(year, 0, 1), 1);

  getWeek = (year: number, week: number): Date => startOfISOWeek(setISOWeek(tzd(year, 5, 1), week));

  isDisabled() {
    //@ts-expect-error Inheritance
    return now() <= this.getStatisticsFinishedDate(this.year);
  }
}

class TimeLine extends Chart {
  getStatisticsFinishedDate = (): Date => {
    if (this.granularity === 'month') {
      return addHours(this.getMonth(this.year, '02'), 8);
    }

    return addHours(this.getWeek(this.year, 2), 8);
  };

  getChartDate = (): Date => {
    if (this.granularity === 'month') {
      return this.getMonth(this.year, '01');
    }
    const startWeek = this.getWeek(this.year, 1);
    const minStartWeek = tzd(2013, 0, 1);

    return startWeek < minStartWeek ? minStartWeek : startWeek;
  };

  isDisabled = (): boolean => super.isDisabled();
}

class YearSeries extends TimeLine {}

class Quarter extends Chart {
  getStatisticsFinishedDate = (year: number): Date => addHours(this.getEndOfQuarter(year, 1), 8);

  getChartDate = (): Date => this.getMonth(this.year, '01');

  isDisabled = (): boolean => super.isDisabled();
}

class YearSummary extends Chart {
  getStatisticsFinishedDate = (year: number): Date => addHours(this.getNextYear(year), 8);

  getChartDate = (): Date => this.getMonth(this.year, '01');

  isDisabled = (): boolean => super.isDisabled();
}

const chartTypes = {
  line: TimeLine,
  year: YearSeries,
  quarter: Quarter,
  yearsummary: YearSummary,
};

export const isYearDisabled = (year: number, granularity: ChartGranularity, chartType: ChartType): boolean => {
  const ChartClass = chartTypes[chartType];
  const chart = new ChartClass(year, granularity, chartType);

  return chart.isDisabled();
};

export const getChartStartDate = (year: number, granularity: ChartGranularity, chartType: ChartType): string => {
  const ChartClass = chartTypes[chartType];
  const chart = new ChartClass(year, granularity, chartType);
  return fmt(chart.getChartDate(), 'yyyy-MM-dd');
};

export const getStartYear = (years: number): number => now().getFullYear() - years;
